import React, { useState } from "react";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Tabs,
  Tab,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Avatar,
  Rating,
} from "@mui/material";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import SpeedIcon from "@mui/icons-material/Speed";
import SecurityIcon from "@mui/icons-material/Security";
import AnalyticsIcon from "@mui/icons-material/Analytics";
import InventoryIcon from "@mui/icons-material/Inventory";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import MobileFriendlyIcon from "@mui/icons-material/MobileFriendly";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import GroupIcon from "@mui/icons-material/Group";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import SupportIcon from "@mui/icons-material/Support";
import PricingIcon from "@mui/icons-material/AttachMoney";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import StarIcon from "@mui/icons-material/Star";
import TimelineIcon from "@mui/icons-material/Timeline";
import IntegrationInstructionsIcon from "@mui/icons-material/IntegrationInstructions";
import HardwareIcon from "@mui/icons-material/Hardware";
import FileDownloadIcon from "@mui/icons-material/FileDownload";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`simple-tabpanel-${index}`}
      aria-labelledby={`simple-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

const theme = createTheme({
  palette: {
    primary: {
      main: "#2563eb", // Modern blue
      light: "#60a5fa",
      dark: "#1d4ed8",
    },
    secondary: {
      main: "#7c3aed", // Modern purple
      light: "#a78bfa",
      dark: "#5b21b6",
    },
    background: {
      default: "#ffffff",
      paper: "#f8fafc",
    },
    text: {
      primary: "#1e293b",
      secondary: "#64748b",
    },
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontWeight: 700,
      letterSpacing: "-0.02em",
    },
    h2: {
      fontWeight: 700,
      letterSpacing: "-0.01em",
    },
    h3: {
      fontWeight: 600,
    },
    h4: {
      fontWeight: 600,
    },
    h5: {
      fontWeight: 600,
    },
    h6: {
      fontWeight: 600,
    },
    button: {
      textTransform: "none",
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          padding: "8px 24px",
          boxShadow: "none",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
        contained: {
          boxShadow: "none",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow:
            "0 1px 3px 0 rgb(0 0 0 / 0.1), 0 1px 2px -1px rgb(0 0 0 / 0.1)",
          transition: "all 0.2s ease-in-out",
          "&:hover": {
            boxShadow:
              "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
            transform: "translateY(-4px)",
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          textTransform: "none",
          fontWeight: 500,
        },
      },
    },
    MuiAccordion: {
      styleOverrides: {
        root: {
          boxShadow: "none",
          "&:before": {
            display: "none",
          },
        },
      },
    },
    MuiAccordionSummary: {
      styleOverrides: {
        root: {
          backgroundColor: "#f8fafc",
          borderRadius: "8px",
          marginBottom: "8px",
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: "6px",
          fontWeight: 500,
        },
      },
    },
  },
});

function App() {
  const [tabValue, setTabValue] = useState(0);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box>
        {/* Hero Section */}
        <Box
          sx={{
            background:
              "linear-gradient(135deg, rgba(37, 99, 235, 0.95) 0%, rgba(29, 78, 216, 0.95) 100%)",
            color: "white",
            py: 12,
            textAlign: "center",
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "url('/images/rfid_banner.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.2,
              zIndex: 0,
            },
            "&::after": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 50% 50%, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0) 70%)",
              zIndex: 1,
            },
          }}
        >
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 2 }}>
            <Typography
              variant="h1"
              component="h1"
              gutterBottom
              sx={{
                fontSize: { xs: "2.5rem", md: "3.5rem" },
                fontWeight: 800,
                lineHeight: 1.2,
                mb: 3,
                position: "relative",
              }}
            >
              RFID Asset Tracking Solution
            </Typography>
            <Typography
              variant="h5"
              component="h2"
              gutterBottom
              sx={{
                fontSize: { xs: "1.25rem", md: "1.5rem" },
                fontWeight: 400,
                opacity: 0.9,
                mb: 4,
                position: "relative",
              }}
            >
              Comprehensive asset management and tracking system powered by RFID
              technology
            </Typography>
            <Button
              variant="contained"
              color="secondary"
              size="large"
              sx={{
                mt: 4,
                px: 6,
                py: 1.5,
                fontSize: "1.1rem",
                borderRadius: "12px",
                textTransform: "none",
                position: "relative",
                zIndex: 10,
                boxShadow:
                  "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                "&:hover": {
                  transform: "translateY(-2px)",
                  boxShadow:
                    "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
                },
              }}
              onClick={() =>
                window.open(
                  "http://129.146.160.158/",
                  "_blank",
                  "noopener,noreferrer",
                )
              }
            >
              Try Demo Now
            </Button>
          </Container>
        </Box>

        {/* Tabs Section */}
        <Container maxWidth="lg" sx={{ mt: 6 }}>
          <Box
            sx={{
              borderBottom: 1,
              borderColor: "divider",
              "& .MuiTabs-root": {
                minHeight: "64px",
              },
              "& .MuiTab-root": {
                minHeight: "64px",
                fontSize: "1rem",
                fontWeight: 500,
              },
            }}
          >
            <Tabs
              value={tabValue}
              onChange={handleTabChange}
              aria-label="RFID Asset Tracking tabs"
              variant="scrollable"
              scrollButtons="auto"
            >
              <Tab icon={<InfoIcon />} label="Features" />
              <Tab icon={<SupportIcon />} label="Support" />
              <Tab icon={<PricingIcon />} label="Pricing" />
              <Tab icon={<TimelineIcon />} label="Case Studies" />
              <Tab icon={<SecurityIcon />} label="Security" />
              <Tab icon={<HardwareIcon />} label="Products" />
              <Tab icon={<AnalyticsIcon />} label="Software" />
              {/* <Tab icon={<IntegrationInstructionsIcon />} label="Integration" /> */}
            </Tabs>
          </Box>

          {/* Features Tab */}
          <TabPanel value={tabValue} index={0}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
            >
              Comprehensive Asset Management
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              {/* RFID Asset Movement Tracking */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <LocationOnIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        RFID Asset Movement Tracking
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Real-time location updates for all assets" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Timestamp tracking for checkouts/check-ins with user details" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="File attachment support for manuals and documentation" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Inventory Management */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <InventoryIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        Inventory Management
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Automated reorder reminders for low-stock items" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Precise stock count tracking and management" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* RFID Asset Cost Management */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <AttachMoneyIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        RFID Asset Cost Management
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Comprehensive cost tracking for inventory parts" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Detailed cost analysis and reporting" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Mobile RFID Cycle Count */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <MobileFriendlyIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        Mobile RFID Cycle Count
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Handheld RFID reader support for location scanning" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Automatic chart generation and website uploads" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Asset status tracking (found, missing, extra)" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* Remote RFID Reader Setup */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <WifiTetheringIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        Remote RFID Reader Setup
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Easy registration and management of multiple RFID readers" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Centralized monitoring and control system" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>

              {/* User Roles and Group Management */}
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <GroupIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        User Roles and Group Management
                      </Typography>
                    </Box>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Role-based access control for administrators and users" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Flexible group management and permissions" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Additional Features Section */}
            <Box sx={{ mt: 8 }}>
              <Typography variant="h4" component="h3" gutterBottom>
                Technical Specifications
              </Typography>
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Feature</TableCell>
                      <TableCell>Specification</TableCell>
                      <TableCell>Benefits</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    <TableRow>
                      <TableCell>RFID Reader Support</TableCell>
                      <TableCell>
                        Compatible with all major RFID reader brands
                      </TableCell>
                      <TableCell>
                        Flexible hardware integration options
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>Data Storage</TableCell>
                      <TableCell>Cloud-based with local backup</TableCell>
                      <TableCell>Secure and accessible from anywhere</TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell>API Integration</TableCell>
                      <TableCell>
                        RESTful API with comprehensive documentation
                      </TableCell>
                      <TableCell>
                        Easy integration with existing systems
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>
          </TabPanel>

          {/* Support Tab */}
          <TabPanel value={tabValue} index={1}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
            >
              Support & Services
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      24/7 Technical Support
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Round-the-clock assistance" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Expert technical team" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Remote troubleshooting" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Training & Documentation
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Comprehensive user guides" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="On-site training sessions" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Video tutorials" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={4}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Maintenance & Updates
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Regular system updates" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Security patches" />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText primary="Performance optimization" />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Pricing Tab */}
          <TabPanel value={tabValue} index={2}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Custom Solutions for Your Needs
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Tailored to Your Business
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Custom Development"
                          secondary="We develop solutions specifically tailored to your business processes and requirements"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Flexible Integration"
                          secondary="Seamless integration with your existing systems and workflows"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Scalable Solutions"
                          secondary="Our solutions grow with your business, from small operations to enterprise-wide deployments"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card sx={{ height: "100%" }}>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Value-Based Pricing
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Transparent Pricing"
                          secondary="Clear, upfront pricing based on your specific needs and requirements"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="ROI Focused"
                          secondary="Our pricing model ensures you get maximum value and return on investment"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Flexible Terms"
                          secondary="Customizable payment terms and options to suit your budget"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            <Box sx={{ mt: 6, textAlign: "center" }}>
              <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
                Ready to Transform Your Asset Management?
              </Typography>
              <Typography
                variant="body1"
                color="text.secondary"
                paragraph
                sx={{ mb: 4, maxWidth: "600px", mx: "auto" }}
              >
                Contact us today for a personalized consultation and custom
                pricing quote. We'll work with you to create the perfect RFID
                asset tracking solution for your business.
              </Typography>
              <Button
                variant="contained"
                color="primary"
                size="large"
                href="mailto:contact@rfid-asset-tracking.com"
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: "1.1rem",
                  borderRadius: "12px",
                  textTransform: "none",
                  boxShadow:
                    "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
                  },
                }}
              >
                Get Your Custom Quote
              </Button>
            </Box>
          </TabPanel>

          {/* Case Studies Tab */}
          <TabPanel value={tabValue} index={3}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
            >
              Success Stories
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>M</Avatar>
                      <Box>
                        <Typography variant="h6">Manufacturing Corp</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Fortune 500 Manufacturing Company
                        </Typography>
                      </Box>
                    </Box>
                    <Rating value={5} readOnly sx={{ mb: 2 }} />
                    <Typography paragraph>
                      "Implemented RFID tracking across our 50,000+ assets,
                      reducing inventory discrepancies by 95% and saving $2M
                      annually in lost equipment."
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label="95% Reduction in Losses"
                        color="success"
                        sx={{ mr: 1 }}
                      />
                      <Chip label="$2M Annual Savings" color="success" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <Avatar sx={{ bgcolor: "primary.main", mr: 2 }}>H</Avatar>
                      <Box>
                        <Typography variant="h6">Healthcare System</Typography>
                        <Typography variant="body2" color="text.secondary">
                          Regional Hospital Network
                        </Typography>
                      </Box>
                    </Box>
                    <Rating value={5} readOnly sx={{ mb: 2 }} />
                    <Typography paragraph>
                      "Our medical equipment tracking system now provides
                      real-time location data, improving patient care and
                      reducing equipment loss by 80%."
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Chip
                        label="80% Reduced Losses"
                        color="success"
                        sx={{ mr: 1 }}
                      />
                      <Chip label="Improved Patient Care" color="success" />
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Security Tab */}
          <TabPanel value={tabValue} index={4}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
            >
              Enterprise-Grade Security
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Data Protection
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="End-to-End Encryption"
                          secondary="All data is encrypted in transit and at rest using industry-standard protocols"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Regular Security Audits"
                          secondary="Third-party security assessments conducted quarterly"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Compliance"
                          secondary="GDPR, HIPAA, and ISO 27001 compliant"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h5" gutterBottom>
                      Access Control
                    </Typography>
                    <List>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Multi-Factor Authentication"
                          secondary="Required for all user accounts"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Role-Based Access"
                          secondary="Granular permissions based on user roles"
                        />
                      </ListItem>
                      <ListItem>
                        <ListItemIcon>
                          <CheckCircleIcon color="primary" />
                        </ListItemIcon>
                        <ListItemText
                          primary="Audit Logging"
                          secondary="Comprehensive activity tracking and reporting"
                        />
                      </ListItem>
                    </List>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Products Tab */}
          <TabPanel value={tabValue} index={5}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
            >
              Hardware Products
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              paragraph
              sx={{ mb: 6 }}
            >
              Parijat offers high-performance RFID hardware solutions designed
              for industrial automation, logistics, and IoT applications.
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              {/* Product 1: UHF Reader */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <HardwareIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        UHF RFID Reader
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      NGSH-10306
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      High-Performance RAIN (UHF) Reading with Smart
                      Connectivity
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Key Features
                    </Typography>
                    <List sx={{ mb: 2 }}>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Focused 1 Channel Design"
                          secondary="Optimized single RF channel with multiplexer expansion capability"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Reads Passive Temperature+Moisture Tags"
                          secondary="Compatible with Axzon passive UHF sensor tags"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="High Output Power & Global UHF Band"
                          secondary="Configurable 0–30 dBm RF output across 860–930 MHz"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Built-in Smart Connectivity"
                          secondary="Dual core 32 bit MCU with Wi-Fi 802.11 b/g/n and Bluetooth® 4.2"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Edge AI & Local Processing"
                          secondary="Execute filtering, calibration, and encryption at the device"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    </List>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Technical Specifications
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        p: 2,
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      <Typography variant="caption" display="block">
                        Operating Frequency: 860–930 MHz
                      </Typography>
                      <Typography variant="caption" display="block">
                        RF Output Power: 0–30 dBm (adjustable)
                      </Typography>
                      <Typography variant="caption" display="block">
                        Typical Read Range: Up to 10 m
                      </Typography>
                      <Typography variant="caption" display="block">
                        Antenna Port: 1 × U.FL (external antenna)
                      </Typography>
                      <Typography variant="caption" display="block">
                        Processor: Dual core 32 bit, up to 240 MHz
                      </Typography>
                      <Typography variant="caption" display="block">
                        Power: 5V via USB-C
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Use Cases
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}
                    >
                      <Chip
                        label="Industrial Automation"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="Cold Chain"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="Retail & IoT"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="Fleet Management"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FileDownloadIcon />}
                      href="/brochures/ParijatRFIDReader.pdf"
                      download
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        fontSize: "0.95rem",
                      }}
                    >
                      Download Brochure
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Product 2: UHF Reader Multiplexer */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <HardwareIcon
                        sx={{ fontSize: 40, color: "secondary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        UHF Reader Multiplexer
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      color="secondary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      NGS-10309
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      Scalable RFID Expansion with Built-in RF Front-end
                      Intelligence
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Key Features
                    </Typography>
                    <List sx={{ mb: 2 }}>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="4-Port UHF RFID Multiplexer"
                          secondary="Connect up to 4 antennas to a single RFID reader"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Unlimited Expansion"
                          secondary="Daisy-chain multiple multiplexers for infinite antenna scalability"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Built-in Smart Connectivity"
                          secondary="Dual core 32 bit MCU with Wi-Fi 802.11 b/g/n and Bluetooth® 4.2"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Intelligent RF Front-end"
                          secondary="Real-time sensing, learning, optimization, and adaptation"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="AI Driven Tag Localization"
                          secondary="Phase deltas, RSSI gradients, motion detection"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    </List>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Expansion Capability
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        p: 2,
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      <Typography variant="caption" display="block">
                        • 1 multiplexer = 4 channels
                      </Typography>
                      <Typography variant="caption" display="block">
                        • 2 multiplexers = 7 channels
                      </Typography>
                      <Typography variant="caption" display="block">
                        • 3 multiplexers = 10 channels
                      </Typography>
                      <Typography variant="caption" display="block">
                        • 4 multiplexers = 13 channels
                      </Typography>
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Advanced AI Features
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}
                    >
                      <Chip
                        label="Intelligent Antenna Selection"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="RF Environment Learning"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="Predictive Maintenance"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="Anomaly Detection"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    </Box>

                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<FileDownloadIcon />}
                      href="/brochures/ParijatRFIDMUX.pdf"
                      download
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        fontSize: "0.95rem",
                      }}
                    >
                      Download Brochure
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>

          {/* Software Tab */}
          <TabPanel value={tabValue} index={6}>
            <Typography
              variant="h3"
              component="h2"
              textAlign="center"
              gutterBottom
            >
              Software Solutions
            </Typography>
            <Typography
              variant="body1"
              textAlign="center"
              color="text.secondary"
              paragraph
              sx={{ mb: 6 }}
            >
              Parijat offers cutting-edge software solutions for manufacturing
              execution, workflow automation, and real-time visibility across
              your operations.
            </Typography>
            <Grid container spacing={4} sx={{ mt: 4 }}>
              {/* Software 1: ParTraceflow MES */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <AnalyticsIcon
                        sx={{ fontSize: 40, color: "primary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        ParTraceflow MES
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      color="primary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      NGSH-10316
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      The Configurable Workflow & Traceability Engine for Modern
                      Manufacturing
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Key Features
                    </Typography>
                    <List sx={{ mb: 2 }}>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="User-Configurable BPMN Workflows"
                          secondary="Visual drag-and-drop interface for workflow design"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Real-Time Event-Driven Integration"
                          secondary="Connect RFID, PLCs, SCADA, barcode scanners, and more"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Queue-Based Manufacturing Control"
                          secondary="Priority-based dispatching with intelligent queues"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="CAD & Engineering Integration"
                          secondary="AutoCAD files and drawings linked to workflow steps"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="primary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="End-to-End Traceability"
                          secondary="Complete audit trail of who, what, when, where, why, and how"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    </List>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Core Capabilities
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}
                    >
                      <Chip
                        label="Quality Management"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="Test Integration"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="DMN Decision Logic"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="Audit Ready"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                      <Chip
                        label="Edge AI Ready"
                        size="small"
                        variant="outlined"
                        color="primary"
                      />
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Supported Integrations
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        p: 2,
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      <Typography variant="caption" display="block">
                        RFID Readers
                      </Typography>
                      <Typography variant="caption" display="block">
                        PLCs & SCADA Systems
                      </Typography>
                      <Typography variant="caption" display="block">
                        Test Benches & Inspection Stations
                      </Typography>
                      <Typography variant="caption" display="block">
                        Vision Systems
                      </Typography>
                      <Typography variant="caption" display="block">
                        Barcode & QR Code Scanners
                      </Typography>
                      <Typography variant="caption" display="block">
                        External Databases & Enterprise Apps
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<FileDownloadIcon />}
                      href="/brochures/ParijatParTraceflowMES.pdf"
                      download
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        fontSize: "0.95rem",
                      }}
                    >
                      Download Brochure
                    </Button>
                  </CardContent>
                </Card>
              </Grid>

              {/* Software 2: Hospital Management & Automation Solutions */}
              <Grid item xs={12} md={6}>
                <Card
                  sx={{
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
                      <AnalyticsIcon
                        sx={{ fontSize: 40, color: "secondary.main", mr: 2 }}
                      />
                      <Typography variant="h5" component="h3">
                        Hospital Management & Automation
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle1"
                      color="secondary"
                      gutterBottom
                      sx={{ fontWeight: 600 }}
                    >
                      Comprehensive Hospital Solutions
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      paragraph
                    >
                      Real-time visibility. Complete traceability. Centralized
                      control across clinical and non-clinical hospital
                      operations.
                    </Typography>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Core Modules
                    </Typography>
                    <List sx={{ mb: 2 }}>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Product & Item Delivery Systems"
                          secondary="AGV and Pneumatic Tube systems with real-time tracking"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Facility Management & Energy Control"
                          secondary="HVAC, lighting, power, and environmental systems monitoring"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Laundry Management Systems"
                          secondary="RFID-enabled tracking of linens with lifecycle monitoring"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Maintenance & Asset Health"
                          secondary="Predictive and condition-based maintenance for critical assets"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckCircleIcon
                            color="secondary"
                            sx={{ fontSize: 20 }}
                          />
                        </ListItemIcon>
                        <ListItemText
                          primary="Operating Theatre (OT) Resource Management"
                          secondary="Scheduling, utilization tracking, and surgical workflow integration"
                          secondaryTypographyProps={{ variant: "caption" }}
                        />
                      </ListItem>
                    </List>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Operational Features
                    </Typography>
                    <Box
                      sx={{ display: "flex", flexWrap: "wrap", gap: 1, mb: 4 }}
                    >
                      <Chip
                        label="Medical Waste Monitoring"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="Real-Time Dashboards"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="System Integration"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="GAMP Compliance"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                      <Chip
                        label="Legacy System Support"
                        size="small"
                        variant="outlined"
                        color="secondary"
                      />
                    </Box>

                    <Typography
                      variant="h6"
                      sx={{ mt: 3, mb: 2, fontWeight: 600 }}
                    >
                      Key Benefits
                    </Typography>
                    <Box
                      sx={{
                        bgcolor: "background.paper",
                        p: 2,
                        borderRadius: 1,
                        mb: 2,
                      }}
                    >
                      <Typography variant="caption" display="block">
                        • Improved patient throughput and safety
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Reduced operational costs
                      </Typography>
                      <Typography variant="caption" display="block">
                        • End-to-end compliance and audit readiness
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Unified visibility across all departments
                      </Typography>
                      <Typography variant="caption" display="block">
                        • Predictive maintenance and downtime prevention
                      </Typography>
                    </Box>

                    <Button
                      variant="contained"
                      color="secondary"
                      startIcon={<FileDownloadIcon />}
                      href="/brochures/HospitalManagementAutomationSolutions.pdf"
                      download
                      sx={{
                        mt: 2,
                        textTransform: "none",
                        fontSize: "0.95rem",
                      }}
                    >
                      Download Brochure
                    </Button>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          </TabPanel>
        </Container>

        {/* FAQ Section */}
        <Container maxWidth="md" sx={{ mt: 8, mb: 8 }}>
          <Typography
            variant="h3"
            component="h2"
            textAlign="center"
            gutterBottom
          >
            Frequently Asked Questions
          </Typography>
          <Box sx={{ mt: 4 }}>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>How does RFID asset tracking work?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Our system uses RFID tags attached to assets that communicate
                  with RFID readers. The readers capture the tag data and
                  transmit it to our cloud-based platform, providing real-time
                  location and status updates.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>What types of assets can be tracked?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Our system can track virtually any type of asset, including
                  equipment, tools, vehicles, IT assets, and more. We provide
                  specialized RFID tags designed for different environments and
                  use cases.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>How secure is the system?</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  We implement enterprise-grade security measures including
                  end-to-end encryption, multi-factor authentication, and
                  regular security audits. All data is stored in secure,
                  redundant data centers.
                </Typography>
              </AccordionDetails>
            </Accordion>
            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography>
                  Can I integrate with my existing systems?
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography>
                  Yes, we provide comprehensive integration options including
                  RESTful APIs, webhooks, and pre-built connectors for major ERP
                  and asset management systems.
                </Typography>
              </AccordionDetails>
            </Accordion>
          </Box>
        </Container>

        {/* Contact Section */}
        <Box
          sx={{
            background: "linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)",
            py: 12,
            mt: 8,
            position: "relative",
            overflow: "hidden",
            "&::before": {
              content: '""',
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background:
                "radial-gradient(circle at 50% 50%, rgba(37, 99, 235, 0.05) 0%, rgba(37, 99, 235, 0) 70%)",
            },
          }}
        >
          <Container maxWidth="md" sx={{ position: "relative", zIndex: 1 }}>
            <Typography
              variant="h2"
              component="h2"
              textAlign="center"
              gutterBottom
              sx={{
                fontWeight: 700,
                mb: 3,
                background: "linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
              }}
            >
              Get Started Today
            </Typography>
            <Typography
              variant="h6"
              textAlign="center"
              color="text.secondary"
              paragraph
              sx={{
                maxWidth: "600px",
                mx: "auto",
                mb: 6,
                fontSize: "1.25rem",
                lineHeight: 1.6,
              }}
            >
              Transform your asset management with our comprehensive RFID
              solution. Contact us for a personalized demo.
            </Typography>
            <Box sx={{ textAlign: "center", mt: 4 }}>
              <Button
                variant="contained"
                color="primary"
                size="large"
                href="mailto:contact@rfid-asset-tracking.com"
                sx={{
                  px: 6,
                  py: 1.5,
                  fontSize: "1.1rem",
                  borderRadius: "12px",
                  textTransform: "none",
                  boxShadow:
                    "0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow:
                      "0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)",
                  },
                }}
              >
                Contact Us
              </Button>
            </Box>
          </Container>
        </Box>
      </Box>
    </ThemeProvider>
  );
}

export default App;
